import { createContext, useContext, useEffect, useState, type FormEvent, type MouseEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Check, CircleCheck, PencilLine, type LucideIcon } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { markMissingFields, revealMissingField } from '@/lib/required-fields';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Dialog } from './dialog';
import { PageHeader } from './page-header';

interface FormScreenState {
  dirty: boolean;
  leave: (to: string | number) => void;
}

const FormScreenContext = createContext<FormScreenState>({ dirty: false, leave: () => undefined });

export function useFormScreen(): FormScreenState {
  return useContext(FormScreenContext);
}

export function FormScreen({
  title,
  description,
  icon,
  eyebrow,
  backTo = -1,
  dirty = false,
  onSubmit,
  wide,
  aside,
  children,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  eyebrow?: string;
  backTo?: string | number;
  dirty?: boolean;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  wide?: boolean;
  aside?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const common = useTranslation('common');
  const [pendingLeave, setPendingLeave] = useState<string | number | null>(null);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const go = (to: string | number) => {
    if (typeof to === 'number') navigate(to);
    else navigate(to);
  };

  const leave = (to: string | number) => {
    if (dirty) setPendingLeave(to);
    else go(to);
  };

  const body = (
    <div className={cn('grid gap-6', aside && 'lg:grid-cols-[minmax(0,1fr)_18rem]')}>
      <div className="flex min-w-0 flex-col gap-5">{children}</div>
      {aside && <div className="space-y-4">{aside}</div>}
    </div>
  );

  return (
    <FormScreenContext.Provider value={{ dirty, leave }}>
      <div className={cn('mx-auto w-full -mb-2 lg:-mb-4', wide ? 'max-w-6xl' : 'max-w-4xl')}>
        <button
          type="button"
          onClick={() => leave(backTo)}
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground focus-ring rounded-md"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
          {common.t('action.back')}
        </button>
        <PageHeader title={title} description={description} icon={icon} eyebrow={eyebrow} help={false} />
        {onSubmit ? (
          <form onSubmit={onSubmit} noValidate>
            {body}
          </form>
        ) : (
          body
        )}
      </div>
      {pendingLeave !== null && (
        <Dialog
          open
          onClose={() => setPendingLeave(null)}
          title={common.t('form.unsaved_title')}
          description={common.t('form.unsaved_body')}
          footer={
            <>
              <Button variant="outline" onClick={() => setPendingLeave(null)}>
                {common.t('form.keep_editing')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  const to = pendingLeave;
                  setPendingLeave(null);
                  go(to);
                }}
              >
                {common.t('form.discard')}
              </Button>
            </>
          }
        >
          <span className="sr-only">{common.t('form.unsaved_body')}</span>
        </Dialog>
      )}
    </FormScreenContext.Provider>
  );
}

export function FormStateScope({ dirty, children }: { dirty: boolean; children: ReactNode }) {
  const navigate = useNavigate();
  const leave = (to: string | number) => {
    if (typeof to === 'number') navigate(to);
    else navigate(to);
  };
  return <FormScreenContext.Provider value={{ dirty, leave }}>{children}</FormScreenContext.Provider>;
}

export function FormSection({
  title,
  description,
  icon: Icon,
  columns = 2,
  children,
  className,
}: {
  title?: string;
  description?: string;
  icon?: LucideIcon;
  columns?: 1 | 2 | 3;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-xl border bg-card shadow-sm', className)}>
      {(title || description) && (
        <header className="flex items-center gap-3 border-b px-5 py-3">
          {Icon && (
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-4 w-4" aria-hidden />
            </span>
          )}
          <div className="min-w-0 leading-tight">
            {title && <h2 className="text-sm font-semibold leading-tight first-letter:uppercase">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>}
          </div>
        </header>
      )}
      <div
        className={cn(
          'grid gap-x-4 gap-y-4 px-5 py-4',
          columns === 2 && 'sm:grid-cols-2',
          columns === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {children}
      </div>
    </section>
  );
}

export function FormActions({
  submitLabel,
  cancelLabel,
  loading,
  disabled,
  incomplete,
  onCancel,
  cancelTo = -1,
  secondary,
  note,
  destructive,
  bleed = true,
}: {
  submitLabel: string;
  cancelLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  incomplete?: boolean;
  onCancel?: () => void;
  cancelTo?: string | number;
  secondary?: ReactNode;
  note?: ReactNode;
  destructive?: boolean;
  bleed?: boolean;
}) {
  const common = useTranslation('common');
  const { leave, dirty } = useFormScreen();
  const [attempt, setAttempt] = useState<'none' | 'marked' | 'unmarked'>('none');
  const warning = incomplete && attempt !== 'none' ? common.t(attempt === 'marked' ? 'form.fix_marked' : 'form.fix_incomplete') : null;
  const status = warning ?? note ?? (dirty ? common.t('form.unsaved_body') : null);

  const guardSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    if (!incomplete) return;
    event.preventDefault();
    const root = event.currentTarget.closest('form') ?? event.currentTarget.closest('main') ?? document.body;
    const first = markMissingFields(root);
    setAttempt(first ? 'marked' : 'unmarked');
    if (first) revealMissingField(first);
  };
  return (
    <div data-form-footer className={cn('sticky z-10 mt-6 no-print', bleed ? '-bottom-2 lg:-bottom-4' : 'bottom-2 mb-2')}>
      <div
        className={cn(
          'mx-auto flex w-full max-w-4xl flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/70 bg-card/90 px-3 py-2.5 backdrop-blur-md',
          'shadow-[0_12px_32px_-12px_rgb(15_23_42/0.35)] ring-1 ring-black/[0.03] dark:shadow-[0_12px_32px_-12px_rgb(0_0_0/0.8)] dark:ring-white/[0.04]',
          'sm:ps-4',
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5 text-xs text-muted-foreground">
          <span
            className={cn(
              'grid h-7 w-7 shrink-0 place-items-center rounded-full transition-colors',
              warning ? 'bg-destructive/15 text-destructive' : dirty ? 'bg-warning/15 text-warning' : 'bg-success/15 text-success',
            )}
            aria-hidden
          >
            {warning ? <AlertCircle className="h-3.5 w-3.5" /> : dirty ? <PencilLine className="h-3.5 w-3.5" /> : <CircleCheck className="h-3.5 w-3.5" />}
          </span>
          <span role={warning ? 'status' : undefined} className={cn('truncate', warning && 'font-medium text-destructive')}>
            {status ?? common.t('form.all_saved')}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {secondary}
          <Button type="button" variant="outline" onClick={onCancel ?? (() => leave(cancelTo))} disabled={loading} className="rounded-xl">
            {cancelLabel ?? common.t('action.cancel')}
          </Button>
          <Button
            type="submit"
            variant={destructive ? 'destructive' : 'default'}
            loading={loading}
            disabled={disabled}
            onClick={guardSubmit}
            className="min-w-[9rem] rounded-xl px-5 shadow-md shadow-primary/25"
          >
            {!loading && !destructive && <Check className="h-4 w-4" aria-hidden />}
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
